import {
  isValidElement,
  memo,
  useMemo,
  type ComponentType,
  type ReactElement,
  type ReactNode,
} from 'react'
import ReactMarkdown, { defaultUrlTransform, type Components } from 'react-markdown'
import remarkBreaks from 'remark-breaks'
import remarkGfm from 'remark-gfm'
import {
  CITATION_MARKER,
  normalizeUiBlockFences,
  MENTION_HREF_RE,
  looksLikeUiBlock,
  UI_BLOCK_FENCE,
  type MessageCitation,
} from '@hub/contracts'
import { useSmoothText } from '@/lib/use-smooth-text'
import { cn } from '@/lib/utils'
import { useUiBlocksEnabled } from '@/lib/ui-blocks-flag'
import { Citation } from './citations'
import { MarkdownTable, UiBlock } from './ui-blocks/lazy-blocks'
import { nodeText } from './ui-blocks/node-text'
import { UiBlockHostContext, type UiBlockHost } from './ui-blocks/ui-block-host'

const CITE_HREF = '#cite-'

/** react-markdown drops unknown protocols; keep `user:` so the `a` renderer can turn it into a name. */
const urlTransform = (url: string) => (MENTION_HREF_RE.test(url) ? url : defaultUrlTransform(url))

/**
 * Replace `[source: <docId>:<name>]` markers with a link the `a` renderer
 * turns into an inline citation chip (numbered by the message's citations).
 */
function withCitationLinks(content: string, citations: MessageCitation[]): string {
  if (citations.length === 0) return content
  const byDoc = new Map(citations.map((c) => [c.documentId, c]))
  return content.replace(CITATION_MARKER, (_m, docId: string) => {
    const c = byDoc.get(docId.trim())
    return c ? `[${c.index}](${CITE_HREF}${c.index})` : ''
  })
}

/**
 * While streaming, the tail of the text is usually mid-construct. Close the
 * open inline markers / code fence so the parser doesn't flip between
 * "literal asterisks" and "bold" on every frame.
 */
function closeDanglingMarkdown(text: string): string {
  let out = text
  const fences = (out.match(/^```/gm) ?? []).length
  if (fences % 2 === 1) return `${out}\n\`\`\``
  // Only look at the last paragraph for inline markers.
  const tailStart = out.lastIndexOf('\n\n')
  const tail = tailStart === -1 ? out : out.slice(tailStart)
  const count = (re: RegExp) => (tail.match(re) ?? []).length
  if (count(/`/g) % 2 === 1) out += '`'
  if (count(/\*\*/g) % 2 === 1) out += '**'
  else if (count(/(?<!\*)\*(?!\*)/g) % 2 === 1) out += '*'
  // A half-typed citation marker would render as literal text — hide it.
  out = out.replace(/\[source:[^\]]*$/, '')
  return out
}

// Module-scope so identity is stable across renders — a fresh object each
// render forces ReactMarkdown to rebuild its renderer pipeline.
const PLUGINS = [remarkGfm, remarkBreaks]

/**
 * Block-level memoization (assistant-ui's memoizeMarkdownComponents): each
 * element re-renders only when its own hast node changed, so an appended
 * paragraph doesn't re-render the ones above it.
 */
type NodeLike = { properties?: Record<string, unknown>; children?: unknown }
function nodesEqual(prev?: NodeLike, next?: NodeLike): boolean {
  if (!prev || !next) return false
  const strip = (p?: Record<string, unknown>) => {
    if (!p) return {}
    const {
      position: _p,
      data: _d,
      ...rest
    } = p as Record<string, unknown> & {
      position?: unknown
      data?: unknown
    }
    return rest
  }
  return (
    JSON.stringify(strip(prev.properties)) === JSON.stringify(strip(next.properties)) &&
    JSON.stringify(prev.children) === JSON.stringify(next.children)
  )
}
function memoizeComponents(components: Components): Components {
  return Object.fromEntries(
    Object.entries(components).map(([key, value]) => {
      if (!value) return [key, value]
      const Inner = value as ComponentType<Record<string, unknown>>
      const WithoutNode = ({ node: _node, ...props }: Record<string, unknown>) => (
        <Inner {...props} />
      )
      return [key, memo(WithoutNode, (a, b) => nodesEqual(a.node as NodeLike, b.node as NodeLike))]
    })
  ) as Components
}

/** `[@Name](user:id)` / `[Name](user:id)` → `@Name`. */
function mentionLabel(children: ReactNode): string {
  const text = Array.isArray(children)
    ? children.map((c) => (typeof c === 'string' ? c : '')).join('')
    : typeof children === 'string'
      ? children
      : ''
  const name = text.replace(/^@/, '').trim()
  return name ? `@${name}` : '@'
}

/** The body of a ```spec fence (board t5xf), or null for any other `<pre>`. */
function uiBlockSource(children: ReactNode): string | null {
  if (!isValidElement(children)) return null
  const code = children as ReactElement<{ className?: string; children?: ReactNode }>
  if (code.props.className !== `language-${UI_BLOCK_FENCE}`) return null
  const source = nodeText(code.props.children).replace(/\n$/, '')
  return looksLikeUiBlock(source) ? source : null
}

/** Board t5xf: with the organization's flag on, every markdown table is interactive. */
function MessageTable({ children }: { children?: ReactNode }) {
  const upgraded = useUiBlocksEnabled()
  const plain = (
    <div className="my-2 overflow-x-auto">
      <table className="border-border w-full border-collapse border text-sm">{children}</table>
    </div>
  )
  return upgraded ? <MarkdownTable fallback={plain}>{children}</MarkdownTable> : plain
}

function buildComponents(projectId: string, citations: MessageCitation[]): Components {
  const byIndex = new Map(citations.map((c) => [c.index, c]))
  return memoizeComponents({
    p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
    ul: ({ children }) => <ul className="my-2 list-inside list-disc space-y-1">{children}</ul>,
    ol: ({ children }) => <ol className="my-2 list-inside list-decimal space-y-1">{children}</ol>,
    li: ({ children }) => <li className="ml-2">{children}</li>,
    strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
    em: ({ children }) => <em className="italic">{children}</em>,
    a: ({ href, children }) => {
      if (href?.startsWith(CITE_HREF)) {
        const c = byIndex.get(Number(href.slice(CITE_HREF.length)))
        if (c) return <Citation citation={c} projectId={projectId} />
      }
      // The document mention syntax copied from a tool result (board 9s8r):
      // a person's name, never a dead `user:` link.
      if (href && MENTION_HREF_RE.test(href)) {
        return <span className="font-medium">{mentionLabel(children)}</span>
      }
      return (
        <a
          href={href}
          className="text-foreground underline underline-offset-2 hover:text-foreground/80"
          target={href?.startsWith('http') ? '_blank' : undefined}
          rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
        >
          {children}
        </a>
      )
    },
    code: ({ className, children }) => (
      <code
        className={cn(
          'bg-foreground/[0.06] rounded-md px-1.5 py-0.5 font-mono text-[0.85em]',
          className
        )}
      >
        {children}
      </code>
    ),
    pre: ({ children }) => {
      // Spec fences always render, flag or not: history must not degrade to
      // JSON when the flag is turned off.
      const spec = uiBlockSource(children)
      if (spec !== null) return <UiBlock source={spec} />
      return (
        <pre className="bg-foreground/[0.04] my-2 overflow-x-auto rounded-xl p-3 text-xs [&_code]:bg-transparent [&_code]:p-0">
          {children}
        </pre>
      )
    },
    h1: ({ children }) => <h1 className="mt-4 mb-2 text-lg font-bold">{children}</h1>,
    h2: ({ children }) => <h2 className="mt-3 mb-2 text-base font-bold">{children}</h2>,
    h3: ({ children }) => <h3 className="mt-2 mb-1 text-sm font-semibold">{children}</h3>,
    blockquote: ({ children }) => (
      <blockquote className="border-foreground/20 text-foreground/70 my-2 border-s-2 ps-3">
        {children}
      </blockquote>
    ),
    table: ({ children }) => <MessageTable>{children}</MessageTable>,
    thead: ({ children }) => <thead className="bg-muted/50">{children}</thead>,
    th: ({ children }) => (
      <th className="border-border border px-3 py-1.5 text-left font-semibold">{children}</th>
    ),
    td: ({ children }) => <td className="border-border border px-3 py-1.5">{children}</td>,
  })
}

interface MessageMarkdownProps {
  content: string
  projectId: string
  citations?: MessageCitation[]
  /** More text may still arrive — reveal smoothly and show the caret. */
  streaming?: boolean
  className?: string
}

function MessageMarkdownImpl({
  content,
  projectId,
  citations = [],
  streaming = false,
  className,
}: MessageMarkdownProps) {
  const { text, animating } = useSmoothText(content, streaming)
  const live = streaming || animating
  const components = useMemo(() => buildComponents(projectId, citations), [projectId, citations])
  const source = useMemo(() => {
    const linked = normalizeUiBlockFences(withCitationLinks(text, citations))
    return live ? closeDanglingMarkdown(linked) : linked
  }, [text, citations, live])
  const host = useMemo<UiBlockHost>(
    () => ({
      live,
      renderMarkdown: (markdown) => (
        <MessageMarkdown content={markdown} projectId={projectId} citations={citations} />
      ),
    }),
    [live, projectId, citations]
  )

  return (
    <div
      data-slot="message-markdown"
      data-streaming={live || undefined}
      className={cn(
        'text-sm leading-relaxed text-pretty',
        live && '[&>*:last-child]:inline',
        className
      )}
    >
      <UiBlockHostContext.Provider value={host}>
        <ReactMarkdown remarkPlugins={PLUGINS} components={components} urlTransform={urlTransform}>
          {source}
        </ReactMarkdown>
      </UiBlockHostContext.Provider>
      {live && (
        <span
          aria-hidden
          className="-mb-0.5 ml-0.5 inline-block h-4 w-0.5 animate-pulse rounded-full bg-blue-500 dark:bg-blue-400"
        />
      )}
    </div>
  )
}

export const MessageMarkdown = memo(MessageMarkdownImpl)
